import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCategoryList } from '../hooks/useCategoryData';
import { config } from '../config/environment';

// Phase 3: Duplicate SKU Prevention
import { useSKUValidation } from '../hooks/useSKUValidation';
import DuplicateSKUModal from './DuplicateSKUModal';

// Shadcn UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';

// Icons
import { 
  Plus, ArrowRight, ArrowLeft, Check, Package, Ruler, Archive, 
  ShoppingCart, Target, DollarSign, Warehouse, Calendar, Save, X
} from 'lucide-react';

// Use centralized config for backend URL
const BACKEND_URL = config.backendUrl;
const API = `${BACKEND_URL}/api/v1`;
const MOCK_TENANT_ID = 'tenant-123';

const ProductCreateWizard = ({ onSuccess, open: externalOpen, onClose: externalOnClose, hideButton = false }) => {
  // CRITICAL FIX: Safe rendering helper to prevent "Objects are not valid as React child" error
  const renderValue = useCallback((value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return JSON.stringify(value);
    }
    return String(value);
  }, []);

  const [internalOpen, setInternalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Use external open state if provided, otherwise use internal
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const handleSetOpen = (value) => {
    if (externalOnClose && !value) {
      // If external control, call onClose when trying to close
      externalOnClose();
    } else if (externalOpen === undefined) {
      // If internal control, use internal setter
      setInternalOpen(value);
    }
  };
  
  // Brand field state management
  const [brandMode, setBrandMode] = useState('dropdown'); // 'dropdown' or 'custom'
  const [customBrandValue, setCustomBrandValue] = useState('');
  
  // ============================================================
  // COMPONENT-BASED INVENTORY: Raw materials state for dropdown
  // ============================================================
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loadingRawMaterials, setLoadingRawMaterials] = useState(false);
  
  // Fetch raw materials for the component dropdown
  useEffect(() => {
    const fetchRawMaterials = async () => {
      setLoadingRawMaterials(true);
      try {
        const response = await axios.get(`${API}/product-master/raw-materials?tenant_id=${MOCK_TENANT_ID}`);
        setRawMaterials(response.data.raw_materials || []);
      } catch (error) {
        console.error('Error fetching raw materials:', error);
        setRawMaterials([]);
      } finally {
        setLoadingRawMaterials(false);
      }
    };
    
    // Fetch when wizard opens
    if (open) {
      fetchRawMaterials();
    }
  }, [open]);
  
  // ✅ CENTRALIZED: Categories from single source
  const categoryList = useCategoryList();

  // Add CSS styles for the modal portal approach with dark mode support
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Modal backdrop */
      .zb-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.5);
        backdrop-filter: blur(2px);
        z-index: 1000;
      }

      /* Modal shell - Dark mode support */
      .zb-modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(920px, 92vw);
        max-height: min(86vh, 920px);
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0,0,0,.25);
        overflow: hidden;
        z-index: 1001;
      }

      /* Dark mode styles */
      .dark .zb-modal {
        background: #111827;
        border-color: #374151;
        box-shadow: 0 10px 40px rgba(0,0,0,.6);
      }

      /* Scroll area */
      .zb-modal__body {
        overflow: auto;
        max-height: calc(86vh - 104px);
        padding: 16px 20px;
        background: #ffffff;
      }

      .dark .zb-modal__body {
        background: #111827;
      }

      /* Wizard-specific tweaks */
      .zb-wizard .zb-modal__body { 
        padding-top: 8px; 
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // ESC key handling, body scroll lock, and focus management
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        handleSetOpen(false);
      }
    };
    
    // Lock body scroll while modal is open
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Phase 3: SKU Validation Hook
  const {
    validationState,
    conflictData,
    showDuplicateModal,
    debouncedValidate,
    handleDuplicateConflict,
    handleConflictResolution,
    closeDuplicateModal,
    clearValidation
  } = useSKUValidation(MOCK_TENANT_ID);
  
  // Form data with all business-critical fields
  const [formData, setFormData] = useState({
    // General Information
    sku_root: '',
    name: '',
    brand: 'PetYupp',
    category: '',
    subcategory: '',
    description: '',
    upc: '',
    status: 'ACTIVE',
    
    // ============================================================
    // COMPONENT-BASED INVENTORY: Product Type fields
    // ============================================================
    product_type: 'standard',  // standard | raw_material | finished_product
    component: null,           // {sku, qty_per_unit} for finished goods
    unit: 'pieces',            // Unit for raw materials
    current_stock: 0,          // Initial stock
    reorder_point: 0,          // Reorder alert threshold
    
    // Dimensions (Unit)
    dimensions: {
      unit_gross_weight: 0,
      unit_net_weight: 0,
      unit_weight_uom: 'G',
      unit_length: 0,
      unit_width: 0,
      unit_height: 0,
      unit_dim_uom: 'CM'
    },
    
    // Case Pack
    case_pack: {
      case_pack_qty: 1,
      case_gross_weight: 0,
      case_net_weight: 0,
      case_weight_uom: 'KG',
      case_length: 0,
      case_width: 0,
      case_height: 0,
      case_dim_uom: 'CM'
    },
    
    // Purchasing & Planning
    purchasing: {
      ordering_uom: 'PCS',
      gr_processing_time_days: 7,
      supplier_name: '',
      supplier_sku: '',
      minimum_order_qty: 1
    },
    
    planning: {
      reorder_point_qty: 10,
      target_stock_qty: 50,
      preferred_warehouse_id: 'wh-001'
    },
    
    // Sales & Storage & Shelf Life
    sales: {
      selling_uom: 'PCS',
      default_price: 0,
      channel_assignment: 'shopify'
    },
    
    storage: {
      default_warehouse_id: 'wh-001',
      storage_condition: 'ROOM_TEMPERATURE',
      shelf_bin_location: ''
    },
    
    shelf_life: {
      shelf_life_days: null,
      batch_no: '',
      mfg_date: null,
      expiry_date: null
    },
    
    // Packaging (New Step 6)
    packaging: {
      units_per_package: 1,
      package_length: 0,
      package_width: 0,
      package_height: 0,
      package_dim_uom: 'CM',
      package_gross_weight: 0,
      package_weight_uom: 'KG',
      packaging_material: '',
      barcode_gtin: ''
    },
    
    // Pricing (New Step 7 - CRITICAL)
    pricing: {
      pricing_mode: 'manual',
      currency: 'USD',
      // Manual mode fields
      wholesale_price: 0,
      retail_price: 0,
      cost_price: 0,
      // Allocation mode fields
      cogm: 0,
      international_freight: 0,
      import_duties: 0,
      inbound_handling: 0,
      domestic_freight: 0,
      fba_fees: 0,
      storage_cost: 0,
      transport_to_fba: 0,
      ids_percentage: 15,
      selling_price: 0,
      target_margin: 0,
      include_overhead: true
    },
    
    tenant_id: MOCK_TENANT_ID
  });

  const steps = [
    {
      title: 'General Information',
      description: 'Basic product details',
      icon: <Package className="w-5 h-5" />,
      fields: ['sku_root', 'name', 'brand', 'category', 'subcategory', 'description', 'upc']
    },
    {
      title: 'Unit Dimensions',
      description: 'Weight and size per unit',
      icon: <Ruler className="w-5 h-5" />,
      fields: ['dimensions']
    },
    {
      title: 'Case Pack',
      description: 'Case packaging information',
      icon: <Archive className="w-5 h-5" />,
      fields: ['case_pack']
    },
    {
      title: 'Purchasing & Planning',
      description: 'Ordering and reorder settings',
      icon: <ShoppingCart className="w-5 h-5" />,
      fields: ['purchasing', 'planning']
    },
    {
      title: 'Sales & Storage',
      description: 'Sales channels and warehouse',
      icon: <Warehouse className="w-5 h-5" />,
      fields: ['sales', 'storage', 'shelf_life']
    },
    {
      title: 'Packaging',
      description: 'Detailed packaging specifications',
      icon: <Package className="w-5 h-5" />,
      fields: ['packaging']
    },
    {
      title: 'Pricing',
      description: 'Pricing modes and cost allocation',
      icon: <DollarSign className="w-5 h-5" />,
      fields: ['pricing']
    },
    {
      title: 'Review & Confirm',
      description: 'Review all information',
      icon: <Check className="w-5 h-5" />,
      fields: []
    }
  ];

  const weightOptions = [
    { value: 'G', label: 'Grams (G)' },
    { value: 'KG', label: 'Kilograms (KG)' },
    { value: 'LB', label: 'Pounds (LB)' },
    { value: 'OZ', label: 'Ounces (OZ)' }
  ];

  const dimensionOptions = [
    { value: 'MM', label: 'Millimeters (MM)' },
    { value: 'CM', label: 'Centimeters (CM)' },
    { value: 'IN', label: 'Inches (IN)' }
  ];

  const uomOptions = [
    { value: 'PCS', label: 'Pieces (PCS)' },
    { value: 'PACK', label: 'Pack (PACK)' },
    { value: 'SET', label: 'Set (SET)' },
    { value: 'CASE', label: 'Case (CASE)' }
  ];

  const categoryOptions = [
    { value: 'bowls-feeders', label: 'Bowls & Feeders' },
    { value: 'toys', label: 'Toys' },
    { value: 'beds', label: 'Beds' },
    { value: 'leashes-collars', label: 'Leashes & Collars' },
    { value: 'grooming', label: 'Grooming' },
    { value: 'food-treats', label: 'Food & Treats' },
    { value: 'health-wellness', label: 'Health & Wellness' },
    { value: 'apparel', label: 'Apparel' },
    { value: 'carriers-travel', label: 'Carriers & Travel' },
    { value: 'training', label: 'Training' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'unknown', label: 'Unknown' }
  ];

  const storageConditions = [
    { value: 'ROOM_TEMPERATURE', label: 'Room Temperature' },
    { value: 'COOL_DRY', label: 'Cool & Dry' },
    { value: 'REFRIGERATED', label: 'Refrigerated' },
    { value: 'FROZEN', label: 'Frozen' }
  ];

  const brandOptions = [
    { value: 'PetYupp', label: 'PetYupp' },
    { value: 'ZuperBooks', label: 'ZuperBooks' },
    { value: 'House Brand', label: 'House Brand' },
    { value: 'Generic', label: 'Generic' },
    { value: 'Private Label', label: 'Private Label' }
  ];

  const updateFormData = useCallback((section, field, value) => {
    setFormData(prev => {
      if (section) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  }, []);

  // CRITICAL FIX: Clean form data to prevent object rendering errors
  const cleanFormDataForSubmit = (data) => {
    const cleaned = { ...data };
    
    // ============================================================
    // COMPONENT-BASED INVENTORY: Include product type fields
    // ============================================================
    cleaned.product_type = data.product_type || 'standard';
    cleaned.unit = data.unit || 'pieces';
    cleaned.current_stock = parseInt(data.current_stock) || 0;
    cleaned.reorder_point = parseInt(data.reorder_point) || 0;
    
    // Handle component for finished goods
    if (data.product_type === 'finished_product' && data.component) {
      cleaned.component = {
        sku: data.component.sku || '',
        qty_per_unit: parseInt(data.component.qty_per_unit) || 1
      };
    } else {
      cleaned.component = null;
    }
    
    // CRITICAL FIX: Explicitly preserve pricing object
    if (data.pricing) {
      cleaned.pricing = {
        pricing_mode: data.pricing.pricing_mode || 'manual',
        currency: data.pricing.currency || 'USD',
        wholesale_price: parseFloat(data.pricing.wholesale_price) || 0,
        retail_price: parseFloat(data.pricing.retail_price) || 0,
        cost_price: parseFloat(data.pricing.cost_price) || 0,
        // Allocation mode fields
        cogm: parseFloat(data.pricing.cogm) || 0,
        international_freight: parseFloat(data.pricing.international_freight) || 0,
        import_duties: parseFloat(data.pricing.import_duties) || 0,
        inbound_handling: parseFloat(data.pricing.inbound_handling) || 0,
        domestic_freight: parseFloat(data.pricing.domestic_freight) || 0,
        fba_fees: parseFloat(data.pricing.fba_fees) || 0,
        storage_cost: parseFloat(data.pricing.storage_cost) || 0,
        transport_to_fba: parseFloat(data.pricing.transport_to_fba) || 0,
        ids_percentage: parseFloat(data.pricing.ids_percentage) || 15,
        selling_price: parseFloat(data.pricing.selling_price) || 0,
        target_margin: parseFloat(data.pricing.target_margin) || 0,
        include_overhead: data.pricing.include_overhead !== false
      };
    }
    
    // Fields that should be integers
    const integerFields = ['unit_gross_weight', 'unit_net_weight', 'unit_length', 'unit_width', 'unit_height',
                           'case_pack_qty', 'case_gross_weight', 'case_net_weight', 'case_length', 'case_width', 'case_height',
                           'gr_processing_time_days', 'minimum_order_qty', 'reorder_point_qty', 'target_stock_qty',
                           'shelf_life_days', 'default_price'];
    
    // Recursively clean nested objects (SKIP pricing - already handled)
    Object.keys(cleaned).forEach(key => {
      if (key === 'pricing') return; // Skip pricing, already cleaned above
      
      if (cleaned[key] && typeof cleaned[key] === 'object' && !Array.isArray(cleaned[key])) {
        cleaned[key] = { ...cleaned[key] };
        // Clean nested object values
        Object.keys(cleaned[key]).forEach(nestedKey => {
          const value = cleaned[key][nestedKey];
          
          // Convert string numbers to actual integers for numeric fields
          if (integerFields.includes(nestedKey)) {
            if (value === '' || value === null || value === undefined) {
              cleaned[key][nestedKey] = 0;
            } else {
              const parsed = parseInt(value, 10);
              cleaned[key][nestedKey] = isNaN(parsed) ? 0 : parsed;
            }
          }
          // Handle date fields - convert empty strings to null
          else if (nestedKey.includes('date') || nestedKey.includes('Date')) {
            if (value === '' || value === null || value === undefined) {
              cleaned[key][nestedKey] = null;
            } else if (typeof value === 'string') {
              // Keep valid date strings, set invalid ones to null
              cleaned[key][nestedKey] = value.trim() || null;
            }
          }
          // Convert objects to strings if needed
          else if (value && typeof value === 'object' && !Array.isArray(value)) {
            cleaned[key][nestedKey] = JSON.stringify(value);
          } 
          // Handle empty values
          else if (value === null || value === undefined) {
            cleaned[key][nestedKey] = '';
          }
        });
      }
    });
    
    return cleaned;
  };
  
  const validateStep = (stepIndex) => {
    const step = steps[stepIndex];
    const requiredFields = ['sku_root', 'name', 'category'];
    
    if (stepIndex === 0) {
      return requiredFields.every(field => formData[field]?.trim());
    }
    return true; // Other steps are optional for MVP
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Handle conflict resolution actions
  const handleSkuConflictResolution = async (resolution) => {
    try {
      const result = await handleConflictResolution(resolution);
      
      switch (result.action) {
        case 'navigate':
          // Close wizard and navigate to existing product
          handleSetOpen(false);
          toast.success(result.message);
          // TODO: Implement navigation to product detail page
          break;
          
        case 'rename':
          // Update form with new SKU and continue creation
          updateFormData(null, 'sku_root', result.new_sku);
          toast.success(result.message);
          // Try creating again with new SKU
          setTimeout(() => handleSubmit(), 500);
          break;
          
        case 'variant':
          // TODO: Implement variant creation logic
          toast.success(result.message);
          break;
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(0)) {
      toast.error('Please complete required fields in General Information');
      return;
    }

    setLoading(true);
    try {
      // CRITICAL DEBUG: Log original formData before cleaning
      console.log('📝 ORIGINAL FORM DATA (before cleaning):');
      console.log('📝 formData.pricing:', formData.pricing);
      
      // CRITICAL FIX: Clean data to ensure no objects in values
      const cleanedData = cleanFormDataForSubmit(formData);
      
      // CRITICAL DEBUG: Log the exact payload being sent to backend
      console.log('🚀 ========================================');
      console.log('🚀 SENDING TO BACKEND - FULL PAYLOAD:');
      console.log('🚀 ========================================');
      console.log(JSON.stringify(cleanedData, null, 2));
      console.log('🚀 ========================================');
      console.log('🚀 PRICING SECTION CHECK:');
      console.log('🚀 Has pricing key:', 'pricing' in cleanedData);
      console.log('🚀 Pricing type:', typeof cleanedData.pricing);
      if (cleanedData.pricing) {
        console.log('🚀 Pricing object:', cleanedData.pricing);
        console.log('🚀 wholesale_price:', cleanedData.pricing.wholesale_price);
        console.log('🚀 retail_price:', cleanedData.pricing.retail_price);
        console.log('🚀 cost_price:', cleanedData.pricing.cost_price);
      }
      console.log('🚀 ========================================');
      
      const response = await axios.post(`${API}/products/enhanced`, cleanedData);
      
      console.log('✅ Product created successfully:', response.data);
      
      // Extract product data from nested structure
      const productData = response.data.product || response.data;
      console.log('📋 Product details:', {
        sku: productData.sku || productData.sku_root,
        name: productData.name,
        tenant_id: productData.tenant_id
      });
      
      // DEBUGGING: Verify product exists in database
      try {
        const tenantId = localStorage.getItem('tenant_id') || MOCK_TENANT_ID;
        console.log('🔍 Verifying product in database with tenant_id:', tenantId);
        
        const verifyResponse = await axios.get(`${API}/product-master/list`, {
          params: { 
            tenant_id: tenantId,
            page: 1,
            page_size: 200
          },
          headers: { 'X-Tenant-ID': tenantId }
        });
        
        console.log('📊 Total products in database:', verifyResponse.data?.items?.length || 0);
        console.log('🔍 Database verification response items count:', verifyResponse.data?.items?.length);
        
        // Check if our new product is in the list
        const newProductSku = productData.sku || productData.sku_root;
        const foundProduct = verifyResponse.data?.items?.find(p => 
          (p.sku === newProductSku) || (p.sku_root === newProductSku)
        );
        
        if (foundProduct) {
          console.log('✅ NEW PRODUCT FOUND IN LIST:', foundProduct);
        } else {
          console.warn('⚠️ NEW PRODUCT NOT FOUND IN LIST! SKU:', newProductSku);
          console.log('📋 All SKUs in list:', verifyResponse.data?.items?.map(p => p.sku || p.sku_root).join(', '));
        }
      } catch (verifyError) {
        console.error('⚠️ Database verification failed:', verifyError);
      }
      
      toast.success('Product created successfully with pricing components!');
      
      // CRITICAL: Dispatch multiple refresh events with correct product data
      console.log('📢 Dispatching refresh events with product data...');
      const eventData = {
        ...productData,
        id: response.data.id,
        sku: productData.sku || productData.sku_root,
        name: productData.name,
        tenant_id: productData.tenant_id
      };
      
      window.dispatchEvent(new CustomEvent('refreshProductList', { detail: eventData }));
      window.dispatchEvent(new CustomEvent('productCreated', { detail: eventData }));
      console.log('✅ Events dispatched successfully with data:', eventData);
      
      handleSetOpen(false);
      setCurrentStep(0);
      clearValidation(); // Clear any validation state
      
      // Reset form
      setFormData({
        sku_root: '', name: '', brand: 'PetYupp', category: '', subcategory: '',
        description: '', upc: '', status: 'ACTIVE',
        dimensions: { unit_gross_weight: 0, unit_net_weight: 0, unit_weight_uom: 'G', unit_length: 0, unit_width: 0, unit_height: 0, unit_dim_uom: 'CM' },
        case_pack: { case_pack_qty: 1, case_gross_weight: 0, case_net_weight: 0, case_weight_uom: 'KG', case_length: 0, case_width: 0, case_height: 0, case_dim_uom: 'CM' },
        purchasing: { ordering_uom: 'PCS', gr_processing_time_days: 7, supplier_name: '', supplier_sku: '', minimum_order_qty: 1 },
        planning: { reorder_point_qty: 10, target_stock_qty: 50, preferred_warehouse_id: 'wh-001' },
        sales: { selling_uom: 'PCS', default_price: 0, channel_assignment: 'shopify' },
        storage: { default_warehouse_id: 'wh-001', storage_condition: 'ROOM_TEMPERATURE', shelf_bin_location: '' },
        shelf_life: { shelf_life_days: null, batch_no: '', mfg_date: null, expiry_date: null },
        tenant_id: MOCK_TENANT_ID
      });
      
      // Reset brand state
      setBrandMode('dropdown');
      setCustomBrandValue('');
      
      // Call onSuccess callback with created product data
      if (onSuccess) {
        console.log('📢 Calling onSuccess callback');
        onSuccess(response.data);
      }
    } catch (error) {
      // Phase 3: Handle SKU duplicate conflicts (409 status)
      if (error.response?.status === 409 && error.response?.data?.detail?.error === 'duplicate_sku') {
        handleDuplicateConflict(error.response.data.detail, formData.sku_root);
      } else {
        // Handle error message properly - could be string or object
        let errorMessage = 'Failed to create product';
        
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          
          // If detail is an array of validation errors
          if (Array.isArray(detail)) {
            errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } 
          // If detail is an object
          else if (typeof detail === 'object') {
            errorMessage = detail.message || detail.msg || JSON.stringify(detail);
          } 
          // If detail is a string
          else {
            errorMessage = detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // General Information
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sku_root">SKU Root *</Label>
                <div className="relative">
                  <Input
                    id="sku_root"
                    value={formData.sku_root}
                    onChange={(e) => {
                      const newSku = e.target.value;
                      updateFormData(null, 'sku_root', newSku);
                      // Phase 3: Real-time SKU validation
                      if (newSku.trim()) {
                        debouncedValidate(newSku.trim());
                      }
                    }}
                    placeholder="e.g., BOWL-001"
                    className={`font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 ${
                      validationState.isValid === false ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                      validationState.isValid === true ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
                      ''
                    }`}
                  />
                  
                  {/* Validation Status Indicator */}
                  {validationState.isValidating && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  
                  {validationState.isDuplicate && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <div className="h-4 w-4 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">!</span>
                      </div>
                    </div>
                  )}
                  
                  {validationState.isValid === true && !validationState.isDuplicate && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <div className="h-4 w-4 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="text-white w-3 h-3" />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Validation Message */}
                {validationState.isDuplicate && validationState.existingProduct && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    SKU already exists in product: {validationState.existingProduct.name}
                  </p>
                )}
                
                {validationState.error && (
                  <p className="text-sm text-red-600 dark:text-red-400">{validationState.error}</p>
                )}
                
                {validationState.isValid === true && !validationState.isDuplicate && formData.sku_root.trim() && (
                  <p className="text-sm text-green-600 dark:text-green-400">SKU is available</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormData(null, 'name', e.target.value)}
                  placeholder="e.g., Elevated Double Diner"
                  className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="brand">Brand</Label>
                <div className="space-y-1">
                  {brandMode === 'dropdown' ? (
                    <select
                      id="brand"
                      value={formData.brand}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'custom') {
                          setBrandMode('custom');
                          setCustomBrandValue('');
                          updateFormData(null, 'brand', '');
                        } else {
                          updateFormData(null, 'brand', value);
                        }
                      }}
                      className="
                        w-full px-4 py-2
                        bg-white dark:bg-gray-800
                        text-gray-900 dark:text-white
                        border border-gray-300 dark:border-gray-600
                        rounded-lg
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        cursor-pointer
                      "
                      style={{ colorScheme: 'dark' }}
                    >
                      {brandOptions.map(option => (
                        <option 
                          key={option.value} 
                          value={option.value}
                          className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          {option.label}
                        </option>
                      ))}
                      <option 
                        value="custom"
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        + Add Custom Brand
                      </option>
                    </select>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          id="brand-custom"
                          value={customBrandValue}
                          onChange={(e) => {
                            setCustomBrandValue(e.target.value);
                            updateFormData(null, 'brand', e.target.value);
                          }}
                          placeholder="Enter custom brand name"
                          className="flex-1 text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setBrandMode('dropdown');
                            setCustomBrandValue('');
                            updateFormData(null, 'brand', 'PetYupp'); // Reset to default
                          }}
                          className="px-3 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Select a predefined brand or add your own
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={formData.category || ''}
                  onChange={(e) => updateFormData(null, 'category', e.target.value)}
                  className="
                    w-full px-4 py-2
                    bg-white dark:bg-gray-800
                    text-gray-900 dark:text-white
                    border border-gray-300 dark:border-gray-600
                    rounded-lg
                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    cursor-pointer
                  "
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="" disabled className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    Select category
                  </option>
                  {categoryList.map(category => (
                    <option 
                      key={category} 
                      value={category} 
                      className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subcategory">Subcategory</Label>
                <Input
                  id="subcategory"
                  value={formData.subcategory}
                  onChange={(e) => updateFormData(null, 'subcategory', e.target.value)}
                  placeholder="e.g., Elevated Feeders"
                  className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="upc">UPC Code</Label>
                <Input
                  id="upc"
                  value={formData.upc}
                  onChange={(e) => updateFormData(null, 'upc', e.target.value)}
                  placeholder="Optional barcode"
                  className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => updateFormData(null, 'description', e.target.value)}
                placeholder="Product description for marketing and sales"
                rows={3}
                className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none"
              />
            </div>
            
            {/* ============================================================ */}
            {/* COMPONENT-BASED INVENTORY: Product Type Selector */}
            {/* ============================================================ */}
            <Separator className="my-6" />
            
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold text-gray-900 dark:text-white mb-3 block">
                  Product Type *
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Select how this product is tracked in inventory
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {/* Standard Product */}
                  <label className={`
                    p-4 rounded-lg border-2 cursor-pointer text-center transition-all
                    ${formData.product_type === 'standard' 
                      ? 'border-blue-500 bg-blue-500/10 dark:bg-blue-500/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}
                  `}>
                    <input
                      type="radio"
                      name="product_type"
                      value="standard"
                      checked={formData.product_type === 'standard'}
                      onChange={(e) => {
                        updateFormData(null, 'product_type', e.target.value);
                        updateFormData(null, 'component', null);
                      }}
                      className="sr-only"
                    />
                    <span className="text-2xl block mb-2">📦</span>
                    <span className="text-gray-900 dark:text-white font-medium block">Standard</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Regular product</span>
                  </label>
                  
                  {/* Raw Material */}
                  <label className={`
                    p-4 rounded-lg border-2 cursor-pointer text-center transition-all
                    ${formData.product_type === 'raw_material' 
                      ? 'border-orange-500 bg-orange-500/10 dark:bg-orange-500/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}
                  `}>
                    <input
                      type="radio"
                      name="product_type"
                      value="raw_material"
                      checked={formData.product_type === 'raw_material'}
                      onChange={(e) => {
                        updateFormData(null, 'product_type', e.target.value);
                        updateFormData(null, 'component', null);
                      }}
                      className="sr-only"
                    />
                    <span className="text-2xl block mb-2">🧱</span>
                    <span className="text-gray-900 dark:text-white font-medium block">Raw Material</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Bulk inventory</span>
                  </label>
                  
                  {/* Finished Good */}
                  <label className={`
                    p-4 rounded-lg border-2 cursor-pointer text-center transition-all
                    ${formData.product_type === 'finished_product' 
                      ? 'border-green-500 bg-green-500/10 dark:bg-green-500/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}
                  `}>
                    <input
                      type="radio"
                      name="product_type"
                      value="finished_product"
                      checked={formData.product_type === 'finished_product'}
                      onChange={(e) => {
                        updateFormData(null, 'product_type', e.target.value);
                        // Initialize component with empty values
                        updateFormData(null, 'component', { sku: '', qty_per_unit: 1 });
                      }}
                      className="sr-only"
                    />
                    <span className="text-2xl block mb-2">🎁</span>
                    <span className="text-gray-900 dark:text-white font-medium block">Finished Good</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Assembled from raw</span>
                  </label>
                </div>
              </div>
              
              {/* Raw Material fields */}
              {formData.product_type === 'raw_material' && (
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 mt-4 space-y-4 border border-orange-200 dark:border-orange-800">
                  <h4 className="font-medium text-orange-800 dark:text-orange-200 flex items-center gap-2">
                    <span>🧱</span> Raw Material Settings
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 dark:text-gray-300">Unit of Measure</Label>
                      <select
                        value={formData.unit || 'pieces'}
                        onChange={(e) => updateFormData(null, 'unit', e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg"
                      >
                        <option value="pieces">Pieces</option>
                        <option value="units">Units</option>
                        <option value="lbs">Pounds (lbs)</option>
                        <option value="kg">Kilograms (kg)</option>
                        <option value="oz">Ounces (oz)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 dark:text-gray-300">Current Stock</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.current_stock || 0}
                        onChange={(e) => updateFormData(null, 'current_stock', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 dark:text-gray-300">Reorder Point</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.reorder_point || 0}
                        onChange={(e) => updateFormData(null, 'reorder_point', parseInt(e.target.value) || 0)}
                        placeholder="Alert when stock falls below this"
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Finished Good fields */}
              {formData.product_type === 'finished_product' && (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-4 space-y-4 border border-green-200 dark:border-green-800">
                  <h4 className="font-medium text-green-800 dark:text-green-200 flex items-center gap-2">
                    <span>🎁</span> Finished Good - Component Setup
                  </h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Link this product to a raw material. Availability will be calculated automatically.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700 dark:text-gray-300">Raw Material Component *</Label>
                      {loadingRawMaterials ? (
                        <div className="text-gray-500 dark:text-gray-400 py-2">Loading raw materials...</div>
                      ) : rawMaterials.length === 0 ? (
                        <div className="text-amber-600 dark:text-amber-400 text-sm py-2">
                          ⚠️ No raw materials found. Create raw materials first via Import or set product type to Raw Material.
                        </div>
                      ) : (
                        <select
                          value={formData.component?.sku || ''}
                          onChange={(e) => updateFormData(null, 'component', {
                            ...formData.component,
                            sku: e.target.value
                          })}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg"
                          required
                        >
                          <option value="">Select raw material...</option>
                          {rawMaterials.map(rm => (
                            <option key={rm.sku} value={rm.sku}>
                              {rm.sku} - {rm.name} ({rm.current_stock || 0} in stock)
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700 dark:text-gray-300">Quantity per Unit *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.component?.qty_per_unit || 1}
                        onChange={(e) => updateFormData(null, 'component', {
                          ...formData.component,
                          qty_per_unit: parseInt(e.target.value) || 1
                        })}
                        placeholder="e.g., 8 pieces per pack"
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                        required
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        How many raw material pieces go into one sellable unit?
                      </p>
                    </div>
                  </div>
                  
                  {/* Availability Preview */}
                  {formData.component?.sku && (
                    <div className="bg-white dark:bg-gray-800 rounded p-3 mt-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        📊 <strong>Availability Preview:</strong>{' '}
                        {(() => {
                          const rm = rawMaterials.find(r => r.sku === formData.component.sku);
                          const stock = rm?.current_stock || 0;
                          const qtyPerUnit = formData.component.qty_per_unit || 1;
                          const available = Math.floor(stock / qtyPerUnit);
                          return `${stock} raw pieces ÷ ${qtyPerUnit} per pack = ${available} packs available`;
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 1: // Unit Dimensions
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Unit Weight</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Gross Weight</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.dimensions.unit_gross_weight}
                    onChange={(e) => updateFormData('dimensions', 'unit_gross_weight', parseFloat(e.target.value) || 0)}
                    className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Net Weight</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.dimensions.unit_net_weight}
                    onChange={(e) => updateFormData('dimensions', 'unit_net_weight', parseFloat(e.target.value) || 0)}
                    className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Weight Unit</label>
                  <select
                    value={formData.dimensions.unit_weight_uom || ''}
                    onChange={(e) => {
                      console.log('Weight unit selected:', e.target.value);
                      updateFormData('dimensions', 'unit_weight_uom', e.target.value);
                    }}
                    onClick={() => console.log('Weight unit dropdown clicked')}
                    onFocus={() => console.log('Weight unit dropdown focused')}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                      appearance-auto
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Select unit</option>
                    {weightOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Unit Dimensions</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Length</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.dimensions.unit_length}
                    onChange={(e) => updateFormData('dimensions', 'unit_length', parseFloat(e.target.value) || 0)}
                    className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Width</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.dimensions.unit_width}
                    onChange={(e) => updateFormData('dimensions', 'unit_width', parseFloat(e.target.value) || 0)}
                    className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Height</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.dimensions.unit_height}
                    onChange={(e) => updateFormData('dimensions', 'unit_height', parseFloat(e.target.value) || 0)}
                    className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Unit</label>
                  <select
                    value={formData.dimensions.unit_dim_uom || ''}
                    onChange={(e) => {
                      console.log('Dimension unit selected:', e.target.value);
                      updateFormData('dimensions', 'unit_dim_uom', e.target.value);
                    }}
                    onClick={() => console.log('Dimension unit dropdown clicked')}
                    onFocus={() => console.log('Dimension unit dropdown focused')}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                      appearance-auto
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Select unit</option>
                    {dimensionOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Case Pack
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Units per Case</Label>
                <Input
                  type="number"
                  value={formData.case_pack.case_pack_qty}
                  onChange={(e) => updateFormData('case_pack', 'case_pack_qty', parseInt(e.target.value) || 1)}
                  className="text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Case Weight</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Gross Weight</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.case_pack.case_gross_weight}
                    onChange={(e) => updateFormData('case_pack', 'case_gross_weight', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Net Weight</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.case_pack.case_net_weight}
                    onChange={(e) => updateFormData('case_pack', 'case_net_weight', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weight Unit</Label>
                  <select
                    value={formData.case_pack.case_weight_uom || ''}
                    onChange={(e) => {
                      console.log('Case weight unit selected:', e.target.value);
                      updateFormData('case_pack', 'case_weight_uom', e.target.value);
                    }}
                    onClick={() => console.log('Case weight unit dropdown clicked')}
                    onFocus={() => console.log('Case weight unit dropdown focused')}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                      appearance-auto
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Select unit</option>
                    {weightOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Case Dimensions</h3>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Length</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.case_pack.case_length}
                    onChange={(e) => updateFormData('case_pack', 'case_length', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Width</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.case_pack.case_width}
                    onChange={(e) => updateFormData('case_pack', 'case_width', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Height</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.case_pack.case_height}
                    onChange={(e) => updateFormData('case_pack', 'case_height', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <select
                    value={formData.case_pack.case_dim_uom || ''}
                    onChange={(e) => {
                      console.log('Case dimension unit selected:', e.target.value);
                      updateFormData('case_pack', 'case_dim_uom', e.target.value);
                    }}
                    onClick={() => console.log('Case dimension unit dropdown clicked')}
                    onFocus={() => console.log('Case dimension unit dropdown focused')}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                      appearance-auto
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Select unit</option>
                    {dimensionOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Purchasing & Planning
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Purchasing Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ordering UoM</Label>
                  <select
                    value={formData.purchasing.ordering_uom || ''}
                    onChange={(e) => updateFormData('purchasing', 'ordering_uom', e.target.value)}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Select UoM</option>
                    {uomOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Lead Time (Days)</Label>
                  <Input
                    type="number"
                    value={formData.purchasing.gr_processing_time_days}
                    onChange={(e) => updateFormData('purchasing', 'gr_processing_time_days', parseInt(e.target.value) || 7)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Supplier Name</Label>
                  <Input
                    value={formData.purchasing.supplier_name}
                    onChange={(e) => updateFormData('purchasing', 'supplier_name', e.target.value)}
                    placeholder="Supplier or manufacturer"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Minimum Order Qty</Label>
                  <Input
                    type="number"
                    value={formData.purchasing.minimum_order_qty}
                    onChange={(e) => updateFormData('purchasing', 'minimum_order_qty', parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Order Planning</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Reorder Point (Units)</Label>
                  <Input
                    type="number"
                    value={formData.planning.reorder_point_qty}
                    onChange={(e) => updateFormData('planning', 'reorder_point_qty', parseInt(e.target.value) || 10)}
                  />
                  <p className="text-xs text-gray-500">Alert when stock falls below this level</p>
                </div>
                <div className="space-y-2">
                  <Label>Target Stock Level</Label>
                  <Input
                    type="number"
                    value={formData.planning.target_stock_qty}
                    onChange={(e) => updateFormData('planning', 'target_stock_qty', parseInt(e.target.value) || 50)}
                  />
                  <p className="text-xs text-gray-500">Optimal inventory quantity</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Sales & Storage & Shelf Life
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Sales Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Selling UoM</Label>
                  <select
                    value={formData.sales.selling_uom || 'PCS'}
                    onChange={(e) => updateFormData('sales', 'selling_uom', e.target.value)}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    {uomOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Default Selling Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.sales.default_price}
                    onChange={(e) => updateFormData('sales', 'default_price', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Storage & Warehouse</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Storage Condition</Label>
                  <select
                    value={formData.storage.storage_condition || 'ROOM_TEMPERATURE'}
                    onChange={(e) => updateFormData('storage', 'storage_condition', e.target.value)}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    {storageConditions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Shelf/Bin Location</Label>
                  <Input
                    value={formData.storage.shelf_bin_location}
                    onChange={(e) => updateFormData('storage', 'shelf_bin_location', e.target.value)}
                    placeholder="e.g., A1-B2-C3"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Shelf Life (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Shelf Life (Days)</Label>
                  <Input
                    type="number"
                    value={formData.shelf_life.shelf_life_days || ''}
                    onChange={(e) => updateFormData('shelf_life', 'shelf_life_days', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="e.g., 365"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Batch Number</Label>
                  <Input
                    value={formData.shelf_life.batch_no}
                    onChange={(e) => updateFormData('shelf_life', 'batch_no', e.target.value)}
                    placeholder="e.g., BT2024001"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 5: // Packaging
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">📦 Packaging Details</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Units per Package</Label>
                  <Input
                    type="number"
                    value={formData.packaging.units_per_package}
                    onChange={(e) => updateFormData('packaging', 'units_per_package', parseInt(e.target.value) || 1)}
                    placeholder="How many units in one package"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Packaging Material (Optional)</Label>
                  <Input
                    value={formData.packaging.packaging_material}
                    onChange={(e) => updateFormData('packaging', 'packaging_material', e.target.value)}
                    placeholder="e.g., Cardboard, Plastic, Glass"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Package Dimensions</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Measurement Unit</Label>
                  <select
                    value={formData.packaging.package_dim_uom || 'cm'}
                    onChange={(e) => updateFormData('packaging', 'package_dim_uom', e.target.value)}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    {dimensionOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Length ({formData.packaging.package_dim_uom})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.packaging.package_length}
                    onChange={(e) => updateFormData('packaging', 'package_length', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Width ({formData.packaging.package_dim_uom})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.packaging.package_width}
                    onChange={(e) => updateFormData('packaging', 'package_width', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Height ({formData.packaging.package_dim_uom})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.packaging.package_height}
                    onChange={(e) => updateFormData('packaging', 'package_height', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Package Weight</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Weight Unit</Label>
                  <select
                    value={formData.packaging.package_weight_uom || 'kg'}
                    onChange={(e) => updateFormData('packaging', 'package_weight_uom', e.target.value)}
                    className="
                      w-full px-4 py-2
                      bg-white dark:bg-gray-800
                      text-gray-900 dark:text-white
                      border border-gray-300 dark:border-gray-600
                      rounded-lg
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                      cursor-pointer
                    "
                    style={{ colorScheme: 'dark' }}
                  >
                    {weightOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Gross Weight per Package</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.packaging.package_gross_weight}
                    onChange={(e) => updateFormData('packaging', 'package_gross_weight', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Barcode (Optional)</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Barcode / GTIN</Label>
                  <Input
                    value={formData.packaging.barcode_gtin}
                    onChange={(e) => updateFormData('packaging', 'barcode_gtin', e.target.value)}
                    placeholder="Product barcode number"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 6: // Pricing (CRITICAL)
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                💰 Pricing Information
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose how to manage pricing for this product
              </p>
            </div>

            {/* Pricing Mode Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold text-gray-900 dark:text-white">Pricing Mode</Label>
              <div className="grid grid-cols-1 gap-3">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.pricing.pricing_mode === 'manual' 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => updateFormData('pricing', 'pricing_mode', 'manual')}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={formData.pricing.pricing_mode === 'manual'}
                      onChange={() => updateFormData('pricing', 'pricing_mode', 'manual')}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">Manual - Enter costs manually</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">Manually enter wholesale, retail, and cost prices</div>
                    </div>
                  </div>
                </div>
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.pricing.pricing_mode === 'allocation' 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => updateFormData('pricing', 'pricing_mode', 'allocation')}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={formData.pricing.pricing_mode === 'allocation'}
                      onChange={() => updateFormData('pricing', 'pricing_mode', 'allocation')}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">Allocation - System calculates (PO + Overhead + Fulfillment)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">System automatically calculates total cost from purchase orders, overhead, and fulfillment</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <select
                value={formData.pricing.currency || 'USD'}
                onChange={(e) => updateFormData('pricing', 'currency', e.target.value)}
                className="
                  w-full px-4 py-2
                  bg-white dark:bg-gray-800
                  text-gray-900 dark:text-white
                  border border-gray-300 dark:border-gray-600
                  rounded-lg
                  focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  cursor-pointer
                "
                style={{ colorScheme: 'dark' }}
              >
                <option value="USD" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">USD ($) - US Dollar</option>
                <option value="INR" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">INR (₹) - Indian Rupee</option>
                <option value="EUR" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">EUR (€) - Euro</option>
                <option value="GBP" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">GBP (£) - British Pound</option>
                <option value="CAD" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">CAD (C$) - Canadian Dollar</option>
              </select>
            </div>

            {/* Manual Mode Fields */}
            {formData.pricing.pricing_mode === 'manual' && (
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white">Manual Pricing</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Wholesale Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pricing.wholesale_price}
                      onChange={(e) => updateFormData('pricing', 'wholesale_price', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Retail Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pricing.retail_price}
                      onChange={(e) => updateFormData('pricing', 'retail_price', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cost Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pricing.cost_price}
                      onChange={(e) => updateFormData('pricing', 'cost_price', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Allocation Mode Fields */}
            {formData.pricing.pricing_mode === 'allocation' && (
              <div className="space-y-6">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    ℹ️ In Allocation mode, costs can be automatically calculated from Purchase Orders and Finance Module overhead after product creation.
                  </p>
                </div>

                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white">A. Direct Costs (Estimated)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>COGM (Cost of Goods Manufactured)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing.cogm}
                        onChange={(e) => updateFormData('pricing', 'cogm', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>International Freight</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing.international_freight}
                        onChange={(e) => updateFormData('pricing', 'international_freight', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Import Duties</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing.import_duties}
                        onChange={(e) => updateFormData('pricing', 'import_duties', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Inbound Handling</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing.inbound_handling}
                        onChange={(e) => updateFormData('pricing', 'inbound_handling', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 dark:text-white">B. Fulfillment & Marketing</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>FBA/3PL Fees</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing.fba_fees}
                        onChange={(e) => updateFormData('pricing', 'fba_fees', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Storage Cost</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing.storage_cost}
                        onChange={(e) => updateFormData('pricing', 'storage_cost', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>IDS (% of Selling Price)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.pricing.ids_percentage}
                        onChange={(e) => updateFormData('pricing', 'ids_percentage', parseFloat(e.target.value) || 15)}
                        placeholder="15"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Margin %</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.pricing.target_margin}
                        onChange={(e) => updateFormData('pricing', 'target_margin', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Selling Price (Estimated)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.pricing.selling_price}
                    onChange={(e) => updateFormData('pricing', 'selling_price', parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 7: // Review & Confirm
        return (
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white flex items-center gap-2">
                <Check className="w-5 h-5" />
                ✓ Review & Confirm
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Review all information before creating the product
              </p>
            </div>

            {/* Summary Cards */}
            <div className="space-y-4">
              {/* General Information */}
              <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white">✓ General Information</h4>
                  <Button size="sm" variant="outline" onClick={() => setCurrentStep(0)}>Edit</Button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600 dark:text-gray-400">SKU:</span> <span className="font-medium text-gray-900 dark:text-white">{renderValue(formData.sku_root)}</span></div>
                  <div><span className="text-gray-600 dark:text-gray-400">Name:</span> <span className="font-medium text-gray-900 dark:text-white">{renderValue(formData.name)}</span></div>
                  <div><span className="text-gray-600 dark:text-gray-400">Category:</span> <span className="font-medium text-gray-900 dark:text-white">{renderValue(formData.category)}</span></div>
                  <div><span className="text-gray-600 dark:text-gray-400">Brand:</span> <span className="font-medium text-gray-900 dark:text-white">{renderValue(formData.brand)}</span></div>
                </div>
              </div>

              {/* Pricing */}
              <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900 dark:text-white">✓ Pricing</h4>
                  <Button size="sm" variant="outline" onClick={() => setCurrentStep(6)}>Edit</Button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600 dark:text-gray-400">Mode:</span> <span className="font-medium text-gray-900 dark:text-white">{formData.pricing?.pricing_mode === 'manual' ? 'Manual' : 'Allocation'}</span></div>
                  <div><span className="text-gray-600 dark:text-gray-400">Currency:</span> <span className="font-medium text-gray-900 dark:text-white">{renderValue(formData.pricing?.currency)}</span></div>
                  {formData.pricing?.pricing_mode === 'manual' ? (
                    <>
                      <div><span className="text-gray-600 dark:text-gray-400">Wholesale:</span> <span className="font-medium text-gray-900 dark:text-white">${renderValue(formData.pricing?.wholesale_price)}</span></div>
                      <div><span className="text-gray-600 dark:text-gray-400">Retail:</span> <span className="font-medium text-gray-900 dark:text-white">${renderValue(formData.pricing?.retail_price)}</span></div>
                    </>
                  ) : (
                    <>
                      <div><span className="text-gray-600 dark:text-gray-400">COGM:</span> <span className="font-medium text-gray-900 dark:text-white">${renderValue(formData.pricing?.cogm)}</span></div>
                      <div><span className="text-gray-600 dark:text-gray-400">Selling Price:</span> <span className="font-medium text-gray-900 dark:text-white">${renderValue(formData.pricing?.selling_price)}</span></div>
                    </>
                  )}
                </div>
              </div>

              {/* Summary Alert */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 mt-4">
                <p className="text-sm text-green-800 dark:text-green-200">
                  ✅ All required information has been entered. Click Create Product to save.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  // Backdrop click handler
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleSetOpen(false);
    }
  };

  // Close handler
  const handleClose = () => {
    handleSetOpen(false);
  };

  return (
    <>
      {/* Trigger Button - Only show if not hidden (hideButton prop) */}
      {!hideButton && (
        <Button 
          data-testid="create-product-wizard-btn" 
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold border-0 shadow-lg hover:shadow-xl transition-all duration-200 gap-2 px-4 py-2"
          style={{ 
            color: 'white', 
            backgroundColor: '#2563eb',
            backgroundImage: 'none'
          }}
          onClick={() => handleSetOpen(true)}
        >
          <Plus className="w-4 h-4" style={{ color: 'white' }} />
          <span style={{ color: 'white', fontWeight: '600' }}>Product Wizard</span>
        </Button>
      )}

      {/* Portal Modal */}
      {open && createPortal(
        <div 
          className="zb-modal-backdrop" 
          role="dialog" 
          aria-modal="true" 
          onClick={handleBackdropClick}
        >
          <div className="zb-modal zb-wizard" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
                  <Package className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  Create New Product - Step {currentStep + 1} of {steps.length}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {steps[currentStep].title}: {steps[currentStep].description}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label="Close modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="zb-modal__body">
              {/* Progress Bar */}
              <div className="mb-6">
                <Progress value={progress} className="w-full" />
                <div className="flex justify-between mt-2">
                  {steps.map((step, index) => (
                    <div key={index} className={`flex items-center gap-1 text-xs ${
                      index <= currentStep 
                        ? 'text-blue-600 dark:text-blue-400 font-medium' 
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {step.icon}
                      <span className="hidden sm:inline">{step.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="min-h-[400px]">
                {renderStepContent()}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className="bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              </div>
              
              <div className="flex gap-2">
                {currentStep < steps.length - 1 ? (
                  <Button 
                    onClick={nextStep}
                    className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white disabled:bg-gray-400 dark:disabled:bg-gray-600"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Create Product
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.getElementById('portal-root') || document.body
      )}
      
      {/* Phase 3: Duplicate SKU Conflict Modal */}
      <DuplicateSKUModal
        open={showDuplicateModal}
        onClose={closeDuplicateModal}
        conflictData={conflictData}
        originalSKU={formData.sku_root}
        onResolveAction={handleSkuConflictResolution}
      />
    </>
  );
};

export default ProductCreateWizard;